import React from 'react'
import { notification } from 'antd'
import moment from 'moment-timezone'
import UAParser from 'ua-parser-js'
import sha256 from 'crypto-js/sha256'
import md5 from 'crypto-js/md5'
import HmacSHA1 from 'crypto-js/hmac-sha1'
import { getUserEmail, getUserId } from 'utils/authLocalStorage'
import _ from 'lodash'
import { parse } from 'date-fns'

export const showNotification = ({ type, title, message }) => {
  notification[type]({
    message: title,
    description: message,
  })
}

export const elementIntoView = (id, delay, block) => {
  setTimeout(() => {
    const element = document.getElementById(id)
    if (element)
      try {
        element.scrollIntoView({ behavior: 'smooth', block: block || 'center' })
      } catch (e) {
        element.scrollIntoView(false)
      }
  }, delay || 1000)
}

export const formatFullName = (name) => {
  if (!name) return ''
  const [f, l] = name.split(' ')
  if (!l) return f
  return `${f} ${l[0]}.`
}

export const getFirstName = (name) => {
  if (!name) return ''
  const f = name.split(' ')[0]
  return f
}

export const numberToLocalString = (num = 0) => {
  return (num || 0).toLocaleString()
}

export const checkFBLoginStatus = () =>
  window.FB &&
  window.FB.getLoginStatus(function (response) {
    console.log('FB resp: ', response)
  })

const onGoogleSignIn = (cb) => (googleUser) => {
  if (!window.googleAuth.isSignedIn.get()) return
  try {
    var profile = {
      accessToken: googleUser.getAuthResponse().id_token,
      // accessToken: googleUser.getAuthResponse().access_token,
      userId: googleUser.getId(),
    }
    cb(profile)
  } catch (e) {
    console.warn(e)
  }
}

export const onClickGoogleLogin = (cb) => {
  const _refreshLogin = () => {
    // window.googleAuth.currentUser.listen(() => {})
    // window.googleAuth.signOut()
    // window.googleAuth.currentUser.listen(onGoogleSignIn(cb))
    window.googleAuth.signIn()
  }
  if (window.googleAuth) {
    _refreshLogin()
  } else {
    const gapi = window.gapi
    if (!gapi) return
    gapi.load('auth2', () => {
      gapi.auth2
        .init({
          client_id:
            '221214607499-sdron1v6lup784kf4te40tfk2nhopa5v.apps.googleusercontent.com',
          scope: 'profile email',
        })
        .then((googleAuth) => {
          window.googleAuth = googleAuth
          window.googleAuth.currentUser.listen(onGoogleSignIn(cb))
          _refreshLogin()
        })
    })
  }
}

export const formatAgeFromDOB = (dob) => {
  const _y = Math.floor(moment.duration(Date.now() - dob).years() / 10) * 10
  return _y ? _y + 's' : ''
}

export const lazyImport = (view) => {
  try {
    return React.lazy(() => retry(() => view()))
  } catch (e) {
    return null
  }
}

const retry = (fn, retriesLeft = 5, interval = 1000) => {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            reject(error)
          } else {
            retry(fn, retriesLeft - 1, interval).then(resolve, reject)
          }
        }, interval)
      })
  })
}

export const firstUpperCase = (str) => {
  return str.toLowerCase().replace(/( |^)[a-z]/g, (L) => L.toUpperCase())
}

export const isChrome = () => {
  return (
    !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime)
  )
}

export const generateAlphanumericPassword = () => {
  var result = ''
  result = Math.random().toString(36).slice(2)
  if (result.length < 10) {
    const length = 10 - result.length
    const chars =
      '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
    for (var i = length; i > 0; --i)
      result += chars[Math.floor(Math.random() * chars.length)]
  } else if (result.length > 10) {
    result = result.slice(0, 10)
  }
  return result
}

export const getDeviceType = () => {
  const u = navigator.userAgent
  const isAndroid = u.indexOf('Android') > -1 || u.indexOf('Linux') > -1
  if (isAndroid) {
    return 'Android'
  }
  const isIOS = (/iPad|iPhone|iPod/.test(u) || isiPad()) && !window.MSStream
  if (isIOS) {
    return 'iOS'
  }
  return 'Unknown'
}

const isiPad = () => {
  const parser = new UAParser()
  const result = parser.getResult()
  if (result.device.model === 'iPad') {
    return true
  }
  return false
}

export const isMobile = () => {
  const deviceType = getDeviceType()
  return deviceType === 'Android' || deviceType === 'iOS'
}

export const updateDataLayer = (source) => {
  if (window.dataLayer) {
    const userId = getUserId()
    const email = getUserEmail()
    let encryptedEmail = ''
    if (!_.isEmpty(email)) {
      encryptedEmail = sha256(email).toString()
    }
    window.dataLayer.push({
      FOuserId: userId || '',
      FOuserEmail: encryptedEmail || '',
    })
  }
}

export const toDate = (date) => {
  return parse(date, 'yyyy-MM-dd HH:mm:ss xx', new Date())
}

export const getBMI = (weight, height) => {
  return Math.round((weight / Math.pow(height, 2)) * 703 * 10) / 10
}

// CARE-137 (Onboarding: default fitness goal & target weight from BMI, CMS
// Access flow). Derives the default FitOn fitness goal from the height/weight
// captured during onboarding:
//   BMI >= 25 → 'Lose weight', with target weight (goalNumber) set to the
//               weight at the top of the healthy range (BMI 24.9), in the
//               user's weight unit;
//   BMI < 25  → 'Be more active', no target weight.
// Normalized to metric (kg, m) internally per the ticket, then converted back
// — so all four weight/height unit combinations (lbs/kg × inch/cm) share one
// formula. goalName/goalNumber/goalUnit are the same PUT /user/goal fields
// fiton-web's onboarding sends (goal values from its User.js goalList — these
// are defaults only; the user can still change them in onboarding).
const METERS_PER_INCH = 0.0254
const METERS_PER_CM = 0.01
const KG_PER_LB = 0.453592
const HEALTHY_BMI_UPPER_BOUND = 24.9

export const getDefaultGoalFromBMI = ({
  weight,
  weightUnit = 'lbs',
  height,
  heightUnit = 'inch',
}) => {
  const heightMeters =
    height * (heightUnit === 'cm' ? METERS_PER_CM : METERS_PER_INCH)
  const weightKg = weightUnit === 'kg' ? weight : weight * KG_PER_LB
  const bmi = weightKg / Math.pow(heightMeters, 2)
  if (bmi < 25) {
    return { goalName: 'Be more active' }
  }
  const targetWeightKg = HEALTHY_BMI_UPPER_BOUND * Math.pow(heightMeters, 2)
  const targetWeight =
    weightUnit === 'kg' ? targetWeightKg : targetWeightKg / KG_PER_LB
  return {
    goalName: 'Lose weight',
    goalNumber: Math.round(targetWeight * 10) / 10,
    goalUnit: weightUnit,
  }
}

export const getPlaceDetailByGoogle = (placeId, cb) => {
  if (!window.google.maps) return
  const map = new window.google.maps.Map(document.getElementById('google-map'))
  const googleMapService =
    window.googleMapService ?? new window.google.maps.places.PlacesService(map)
  window.googleMapService = googleMapService
  var request = {
    placeId,
  }
  googleMapService.getDetails(request, cb)
}

export const getGymAddress = (gym = {}) => {
  const [studioAddress, city, stateAndPostalCode, country] =
    gym?.formatted_address?.split(', ') || []
  let [state, postalCode] = stateAndPostalCode?.split(' ') || []
  const getBackupAddress = (type, short = false) => {
    return (
      gym?.address_components?.find((c) => c?.types?.includes(type))?.[
        short ? 'short_name' : 'long_name'
      ] ?? ''
    )
  }

  return {
    formattedAddress: gym?.formatted_address?.includes(gym.name)
      ? gym?.formatted_address
      : `${gym?.formatted_address}`,
    studioName: gym.name,
    studioAddress: studioAddress ?? '',
    city: city ?? getBackupAddress('locality') ?? '',
    country: getBackupAddress('country', true) ?? country ?? '',
    state: state ?? getBackupAddress('administrative_area_level_1') ?? '',
    postalCode: postalCode ?? getBackupAddress('postal_code') ?? '',
  }
}

export const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10)

  let formatted = ''
  if (digits.length <= 3) {
    formatted = digits
  } else if (digits.length <= 6) {
    formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`
  } else {
    formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  }

  return formatted
}

/**
 * Encrypts a string value using MD5 algorithm
 * @param {string} value - The string value to encrypt
 * @returns {string} The MD5 hash of the input value as a hexadecimal string
 */
export const encryptWithMd5 = (value) => {
  if (!value) return ''
  // Coerce explicitly: crypto-js md5 does NOT stringify non-string input (it
  // treats it as a WordArray), so md5(146782) !== md5('146782'). Callers pass
  // numeric ids (e.g. user.id straight from the FitOn login response) — the
  // old URL-based handoff only worked because the id was serialized through
  // a query string first.
  return md5(String(value)).toString()
}

// Builds the {u, e, t, h} signed auto-login payload fiton-care-web's backend
// expects at POST /user/bind/fiton-user/by/auto-login (see
// apis/FitOnCareAPI.js's userAutoLoginPromise). u/e are md5 hashes of the raw
// FitOn user id/email, t is a timestamp, and h is an HmacSHA1 signature over
// the raw (unhashed) id+email+timestamp, keyed by REACT_APP_autoLoginSecret.
export const getAutoLoginPayload = ({ fitOnUserId, fitOnEmail }) => {
  if (!fitOnUserId || !fitOnEmail) return {}

  // String() for the same crypto-js non-string pitfall as encryptWithMd5 above.
  let userMd5 = md5(String(fitOnUserId))
  let emailMd5 = md5(String(fitOnEmail))
  let timeStamp = Date.now().toString()
  let signature = `${fitOnUserId}${fitOnEmail}${timeStamp}`
  let hmac = HmacSHA1(
    signature,
    process.env.REACT_APP_autoLoginSecret
  ).toString()
  return { u: userMd5, e: emailMd5, t: timeStamp, h: hmac }
}

export const getAutoLoginParams = ({ fitOnUserId, fitOnEmail }) => {
  if (!fitOnUserId || !fitOnEmail) return ''
  const { u, e, t, h } = getAutoLoginPayload({ fitOnUserId, fitOnEmail })
  return `u=${u}&e=${e}&t=${t}&h=${h}`
}
